import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Slider } from 'primereact/slider';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api from '../../services/api';
import {
  BORDER_CITIES_BY_STATE,
  BORDER_OPTIONS,
  BORDER_STATES,
} from '../../constants/constants';
import './LiveTracksManager.css';

function resolveStateCode(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  const upper = v.toUpperCase();
  const byCode = BORDER_STATES.find((s) => s.code.toUpperCase() === upper);
  if (byCode) return byCode.code;
  const byName = BORDER_STATES.find((s) => s.name.toLowerCase() === v.toLowerCase());
  return byName?.code || v;
}

function stateDisplayName(raw) {
  const code = resolveStateCode(raw);
  if (!code) return 'Sin estado';
  return BORDER_STATES.find((s) => s.code === code)?.name || String(raw || '').trim() || 'Sin estado';
}

function cityDisplayName(stateRaw, cityRaw) {
  const stateCode = resolveStateCode(stateRaw);
  const city = String(cityRaw || '').trim();
  if (!city) return 'Ciudad';
  const cities = BORDER_CITIES_BY_STATE[stateCode] || [];
  const byCode = cities.find((c) => c.code === city);
  if (byCode) return byCode.name;
  const byName = cities.find((c) => c.name.toLowerCase() === city.toLowerCase());
  return byName?.name || city;
}

function borderDisplayName(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  return BORDER_OPTIONS.find((b) => b.code === v || b.code === v.toLowerCase())?.name || v;
}

function formatElapsed(startedAt, nowMs, endedAt) {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : nowMs;
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function transportLabel(mode) {
  if (mode === 'pedestrian') return 'Peatonal';
  if (mode === 'commercial') return 'Comercial';
  return 'Auto';
}

function laneLabel(types, fallback) {
  if (fallback) return fallback;
  const first = Array.isArray(types) ? types[0] : types;
  const value = String(first || 'standard').toLowerCase();
  if (value === 'sentri') return 'SENTRI';
  if (value === 'ready' || value === 'ready_lane') return 'Ready Lane';
  if (value === 'fast') return 'FAST';
  return 'General';
}

function TrackMetaBadges({ row }) {
  return (
    <div className="lt-badges">
      <Tag value={row.mobileName || '—'} severity="secondary" />
      <Tag value={transportLabel(row.transportMode)} />
      <Tag value={laneLabel(row.laneTypes, row.laneLabel)} />
    </div>
  );
}

function endReasonLabel(reason) {
  if (reason === 'crossed') return 'Cruzó';
  if (reason === 'user_confirmed') return 'Ya crucé';
  if (reason === 'admin') return 'Admin';
  if (reason === 'left_queue' || reason === 'left_geofence') return 'Salió de la fila';
  if (reason === 'stale') return 'Stale';
  if (reason === 'manual') return 'Incompleto';
  return reason || '—';
}

export default function LiveTracksManager() {
  const toast = useRef(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [cityGeoId, setCityGeoId] = useState(null);
  const [slotKey, setSlotKey] = useState(null);
  const [nickname, setNickname] = useState('');
  const [elapsedMinutes, setElapsedMinutes] = useState(8);
  const [expectedWaitMinutes, setExpectedWaitMinutes] = useState(20);
  const [pathProgress, setPathProgress] = useState(0.15);
  const [pageQuery, setPageQuery] = useState('');
  const [crossingQuery, setCrossingQuery] = useState('');
  const [filterTransport, setFilterTransport] = useState('');
  const [filterLane, setFilterLane] = useState('');

  const loadSlots = useCallback(async () => {
    const res = await api.get('/live_tracks/slots');
    setSlots(res.data?.items || []);
  }, []);

  const loadTracks = useCallback(async () => {
    const res = await api.get('/live_tracks');
    setTracks(res.data?.items || []);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await Promise.all([loadSlots(), loadTracks()]);
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'No se pudieron cargar los recorridos',
          detail: error?.response?.data?.error || error.message,
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadSlots, loadTracks]);

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    const poll = setInterval(() => {
      loadTracks().catch(() => {});
    }, 4000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [loadTracks]);

  const cityOptions = useMemo(() => {
    const seen = new Map();
    for (const slot of slots) {
      const id = slot.cityGeoId;
      if (!seen.has(id)) {
        const cityName = cityDisplayName(slot.state, slot.city);
        const stateName = stateDisplayName(slot.state);
        seen.set(id, {
          label: `${cityName} · ${stateName}`,
          value: id,
          search: `${cityName} ${stateName} ${slot.city} ${slot.state} ${borderDisplayName(slot.border)}`.toLowerCase(),
        });
      }
    }
    const q = pageQuery.trim().toLowerCase();
    return [...seen.values()].filter((opt) => !q || opt.search.includes(q));
  }, [slots, pageQuery]);

  const slotOptions = useMemo(() => {
    const q = crossingQuery.trim().toLowerCase();
    return slots
      .filter((slot) => !cityGeoId || slot.cityGeoId === cityGeoId)
      .filter((slot) => {
        if (!q) return true;
        const hay = `${slot.crossingName || ''} ${slot.displayName || ''} ${slot.portNumber || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .filter((slot) => {
        if (!filterTransport) return true;
        const mode = String(slot.transportMode || '').toLowerCase();
        if (filterTransport === 'vehicle') return mode === 'vehicle' || mode === 'passenger';
        return mode === filterTransport;
      })
      .filter((slot) => {
        if (!filterLane) return true;
        const lanes = (slot.laneTypes || []).map((x) => String(x).toLowerCase());
        if (filterLane === 'ready_lane') {
          return lanes.includes('ready_lane') || lanes.includes('ready');
        }
        return lanes.includes(filterLane) || (filterLane === 'standard' && lanes.length === 0);
      })
      .map((slot) => ({
        label: `${slot.crossingName || slot.displayName} · ${transportLabel(
          slot.transportMode
        )} · ${laneLabel(slot.laneTypes)}`,
        value: slot.slotKey,
        cityGeoId: slot.cityGeoId,
      }));
  }, [slots, cityGeoId, crossingQuery, filterTransport, filterLane]);

  const matchesTrackFilters = (row) => {
    if (cityGeoId && row.cityGeoId !== cityGeoId) return false;
    const q = crossingQuery.trim().toLowerCase();
    if (q) {
      const hay = `${row.crossingName || ''} ${row.displayName || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterTransport) {
      const mode = String(row.transportMode || '').toLowerCase();
      const ok =
        filterTransport === 'vehicle'
          ? mode === 'vehicle' || mode === 'passenger'
          : mode === filterTransport;
      if (!ok) return false;
    }
    if (filterLane) {
      const lanes = [row.laneType, ...(row.laneTypes || [])].map((x) => String(x || '').toLowerCase());
      const ok =
        filterLane === 'ready_lane'
          ? lanes.includes('ready_lane') || lanes.includes('ready')
          : lanes.includes(filterLane);
      if (!ok) return false;
    }
    return true;
  };

  const active = tracks.filter((row) => row.active && matchesTrackFilters(row));
  const recent = tracks.filter((row) => !row.active && matchesTrackFilters(row)).slice(0, 12);

  const simulate = async () => {
    if (!cityGeoId || !slotKey) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Elige ciudad y fila',
      });
      return;
    }
    setSaving(true);
    try {
      const slot = slots.find((s) => s.cityGeoId === cityGeoId && s.slotKey === slotKey);
      await api.post('/live_tracks/simulate', {
        cityGeoId,
        slotKey,
        nickname: nickname.trim() || undefined,
        elapsedMinutes,
        expectedWaitMinutes,
        pathProgress,
        city: slot?.city,
        state: slot?.state,
        border: slot?.border,
      });
      setNickname('');
      await loadTracks();
      toast.current?.show({
        severity: 'success',
        summary: 'Recorrido sintético iniciado',
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se pudo simular',
        detail: error?.response?.data?.message || error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const stopTrack = (row) => {
    confirmDialog({
      header: 'Parar recorrido',
      message: `¿Parar el recorrido de ${row.nickname || 'este usuario'}? Dejará de verse en vivo.`,
      acceptLabel: 'Parar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setSaving(true);
        try {
          await api.post(`/live_tracks/${row.id}/stop`);
          await loadTracks();
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'No se pudo parar',
            detail: error?.response?.data?.message || error.message,
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const advanceTrack = async (row, progress) => {
    try {
      await api.post(`/live_tracks/${row.id}/progress`, { progress });
      await loadTracks();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se pudo mover el recorrido',
        detail: error?.response?.data?.message || error.message,
      });
    }
  };

  const stopAllSynthetic = () => {
    confirmDialog({
      header: 'Parar sintéticos',
      message: 'Se detienen todos los recorridos simulados y se publican en el chat.',
      acceptLabel: 'Parar todos',
      rejectLabel: 'Cancelar',
      accept: async () => {
        setSaving(true);
        try {
          await api.post('/live_tracks/stop-all-synthetic');
          await loadTracks();
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'No se pudieron parar',
            detail: error?.response?.data?.error || error.message,
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <div className="lt-page">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="lt-header">
        <div>
          <p className="lt-kicker">Community & Mobile</p>
          <h1 className="lt-title">Live tracks</h1>
          <p className="lt-lead">
            Simula recorridos con timers reales, páralos cuando quieras y al
            terminar se publican en el chat del crossing (transporte + carril).
          </p>
        </div>
        <Button
          label="Parar sintéticos"
          icon="pi pi-stop-circle"
          severity="danger"
          outlined
          disabled={!active.some((row) => row.synthetic)}
          onClick={stopAllSynthetic}
        />
      </div>

      <div className="lt-stats">
        <div className="lt-stat">
          <small>Activos</small>
          <strong>{active.length}</strong>
          <span>incluyendo simulados</span>
        </div>
        <div className="lt-stat">
          <small>Sintéticos</small>
          <strong>{active.filter((row) => row.synthetic).length}</strong>
          <span>no se vencen a los 45s</span>
        </div>
        <div className="lt-stat">
          <small>Filas listas</small>
          <strong>{slots.length}</strong>
          <span>con geocerca y path</span>
        </div>
      </div>

      <section className="lt-card">
        <h3>Filtros</h3>
        <div className="lt-form">
          <label className="lt-field lt-field-wide">
            <span>Buscar estado / ciudad</span>
            <InputText
              value={pageQuery}
              onChange={(e) => setPageQuery(e.target.value)}
              placeholder="Tijuana, Baja California…"
            />
          </label>
          <label className="lt-field lt-field-wide">
            <span>Buscar crossing</span>
            <InputText
              value={crossingQuery}
              onChange={(e) => setCrossingQuery(e.target.value)}
              placeholder="San Ysidro, Otay…"
            />
          </label>
          <label className="lt-field">
            <span>Transport</span>
            <select
              className="p-inputtext p-component w-full"
              value={filterTransport}
              onChange={(e) => setFilterTransport(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="vehicle">Auto</option>
              <option value="pedestrian">Peatonal</option>
              <option value="commercial">Comercial</option>
            </select>
          </label>
          <label className="lt-field">
            <span>Lane</span>
            <select
              className="p-inputtext p-component w-full"
              value={filterLane}
              onChange={(e) => setFilterLane(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="standard">General</option>
              <option value="sentri">SENTRI</option>
              <option value="ready_lane">Ready Lane</option>
              <option value="fast">FAST</option>
            </select>
          </label>
          <div className="lt-actions">
            <Button
              type="button"
              label="Limpiar"
              outlined
              size="small"
              onClick={() => {
                setPageQuery('');
                setCrossingQuery('');
                setFilterTransport('');
                setFilterLane('');
                setCityGeoId(null);
                setSlotKey(null);
              }}
            />
          </div>
        </div>
      </section>

      <section className="lt-card">
        <h3>Simular recorrido</h3>
        {loading ? (
          <ProgressSpinner style={{ width: 28, height: 28 }} />
        ) : (
          <div className="lt-form">
            <label className="lt-field">
              <span>Ciudad</span>
              <Dropdown
                value={cityGeoId}
                options={cityOptions}
                onChange={(e) => {
                  setCityGeoId(e.value);
                  setSlotKey(null);
                }}
                placeholder="Elige ciudad"
              />
            </label>
            <label className="lt-field lt-field-wide">
              <span>Fila</span>
              <Dropdown
                value={slotKey}
                options={slotOptions}
                onChange={(e) => {
                  setSlotKey(e.value);
                  const match = slotOptions.find((opt) => opt.value === e.value);
                  if (match?.cityGeoId) setCityGeoId(match.cityGeoId);
                }}
                placeholder="Crossing · transporte · carril"
                filter
              />
            </label>
            <label className="lt-field">
              <span>Apodo</span>
              <InputText
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Aleatorio si vacío"
              />
            </label>
            <label className="lt-field">
              <span>Ya lleva (min)</span>
              <InputNumber
                value={elapsedMinutes}
                onValueChange={(e) => setElapsedMinutes(e.value ?? 0)}
                min={0}
                max={240}
              />
            </label>
            <label className="lt-field">
              <span>Espera estimada</span>
              <InputNumber
                value={expectedWaitMinutes}
                onValueChange={(e) => setExpectedWaitMinutes(e.value ?? 0)}
                min={0}
                max={300}
              />
            </label>
            <label className="lt-field lt-field-wide">
              <span>Avance en el path ({Math.round(pathProgress * 100)}%)</span>
              <Slider
                value={pathProgress}
                onChange={(e) => setPathProgress(Number(e.value) || 0)}
                min={0}
                max={0.95}
                step={0.05}
              />
            </label>
            <div className="lt-actions">
              <Button
                label="Iniciar simulación"
                icon="pi pi-play"
                loading={saving}
                onClick={simulate}
              />
            </div>
          </div>
        )}
      </section>

      <section className="lt-card">
        <h3>En vivo ahora</h3>
        {active.length === 0 ? (
          <p className="lt-empty">Nadie está compartiendo un recorrido.</p>
        ) : (
          <div className="lt-list">
            {active.map((row) => (
              <article key={row.id} className="lt-row">
                <div>
                  <div className="lt-row-top">
                    <strong>{row.nickname}</strong>
                    {row.synthetic ? <Tag value="Sintético" severity="info" /> : <Tag value="Usuario" />}
                    {row.chatPostedAt ? <Tag value="En chat" severity="success" /> : null}
                  </div>
                  <TrackMetaBadges row={row} />
                  <small>
                    path {Math.round((row.pathProgress || 0) * 100)}% · quedan{' '}
                    {Math.round(row.remainingMeters || 0)} m
                  </small>
                </div>
                <div className="lt-timer">
                  {formatElapsed(row.startedAt, nowMs, row.endedAt)}
                </div>
                <div className="lt-row-actions">
                  <Button
                    label="Casi cruza"
                    size="small"
                    outlined
                    onClick={() => advanceTrack(row, 0.92)}
                  />
                  <Button
                    label="Parar"
                    size="small"
                    severity="danger"
                    onClick={() => stopTrack(row)}
                  />
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {recent.length > 0 ? (
        <section className="lt-card">
          <h3>Recién terminados</h3>
          <div className="lt-list">
            {recent.map((row) => (
              <article key={row.id} className="lt-row ended">
                <div>
                  <div className="lt-row-top">
                    <strong>{row.nickname}</strong>
                    <Tag value={endReasonLabel(row.endReason)} />
                    {row.chatChannelSlug ? <Tag value="Chat" severity="success" /> : null}
                  </div>
                  <TrackMetaBadges row={row} />
                </div>
                <div className="lt-timer">
                  {formatElapsed(row.startedAt, nowMs, row.endedAt)}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
